// Reading a week of programming.
//
// Pure, so the wording of the balance strip and the shape of a column can be
// tested without a browser — and so the strip cannot invent a judgement the
// server did not make. Every count and every "thin" flag is served; this only
// decides how to say it.

import { repsForLine } from './repScheme';

/** Days in the order a training week is read, Monday first. */
export const WEEK_ORDER = [1, 2, 3, 4, 5, 6, 0];

export const SHORT_DAY = {
  0: 'Sun', 1: 'Mon', 2: 'Tue', 3: 'Wed', 4: 'Thu', 5: 'Fri', 6: 'Sat',
};

/**
 * The days, in the order the column layout shows them.
 *
 * # WHY MONDAY FIRST WHEN THE API SAYS SUNDAY FIRST
 *
 * The API indexes days the way every date library does, Sunday at 0, and that is
 * the right thing for it to do. A training week is read Monday to Sunday — the
 * weekend is where the deload sits, not where the week starts — so the reordering
 * belongs here, at the point of display, rather than in a schema everything else
 * has to agree with.
 */
export function orderedDays(week) {
  const byIndex = new Map((week?.days ?? []).map((day) => [day.dayOfWeek, day]));
  return WEEK_ORDER
    .map((index) => byIndex.get(index))
    .filter(Boolean)
    .map((day) => ({ ...day, short: SHORT_DAY[day.dayOfWeek] }));
}

/** Every prescription on a day, flattened across its sessions. */
export function linesOf(day) {
  return (day?.sessions ?? []).flatMap((session) => session.exercises ?? []);
}

/**
 * The density line for a column footer: sets, reps, exercises.
 *
 * # WHY REPS ARE A RANGE
 *
 * A rep scheme is "6-8" or "11,9,7,5" or "45 sec". This once refused a rep total
 * altogether, because collapsing a range to one number invents precision — a
 * summed midpoint reads exactly as exact as a count someone made. A RANGE keeps
 * the refusal's honesty while answering the question the coach is actually
 * asking: four sets of 6-8 is 24–32 reps, said as 24–32, not as 28.
 *
 * The rep figure covers only the sets that prescribe reps. Timed and measured
 * work (durations, distances) is real training but not reps, so it stays in the
 * set count and is left out of the rep range rather than counted as zero — see
 * repsForLine. A day of nothing but timed work therefore shows no rep figure at
 * all, which is correct.
 */
export function densityOf(day) {
  const sets = Number(day?.sets) || 0;
  const exercises = Number(day?.exercises) || 0;
  if (!exercises) return null;

  let low = 0;
  let high = 0;
  let anyReps = false;
  for (const line of linesOf(day)) {
    const reps = repsForLine(line);
    if (reps) {
      low += reps.low;
      high += reps.high;
      anyReps = true;
    }
  }

  const parts = [`${sets} set${sets === 1 ? '' : 's'}`];
  if (anyReps) {
    // An en dash, so a total range does not read as a rep scheme's hyphen.
    parts.push(low === high ? `${low} reps` : `${low}–${high} reps`);
  }
  parts.push(`${exercises} exercise${exercises === 1 ? '' : 's'}`);
  return parts.join(' · ');
}

/**
 * The balance strip's rows.
 *
 * # WHAT THIS DOES NOT DO
 *
 * It does not decide what is thin. The server does, over the whole week, with a
 * minimum volume before it will judge at all — see models.MinSetsToJudge. Two
 * implementations of "is this balanced" is two chances to tell a coach different
 * things on different screens.
 */
export function balanceRows(week) {
  return (week?.balance ?? []).map((pair) => {
    const a = Number(pair.aSets) || 0;
    const b = Number(pair.bSets) || 0;
    const most = Math.max(a, b, 1);
    return {
      name: pair.name,
      thin: pair.thin || '',
      a: { pattern: pair.a, sets: a, share: a / most, thin: pair.thin === pair.a },
      b: { pattern: pair.b, sets: b, share: b / most, thin: pair.thin === pair.b },
    };
  });
}

/** A pattern's label, from the week's own counts. */
export function labelFor(week, pattern) {
  const found = (week?.patterns ?? []).find((p) => p.pattern === pattern);
  return found?.label ?? pattern;
}

/**
 * What the strip says when it should not be trusted.
 *
 * 599 of the corpus's 2,766 exercises are unclassified — arm work, cuff work,
 * carries, conditioning. A week made mostly of those has a balance reading that
 * describes a minority of the training, and saying so is better than drawing a
 * confident bar over it.
 */
export const UNCLASSIFIED_WARNING_SHARE = 0.4;

export function unclassifiedCaveat(week) {
  const patterns = week?.patterns ?? [];
  const total = patterns.reduce((sum, p) => sum + (Number(p.sets) || 0), 0);
  if (!total) return null;

  const unclassified = patterns.find((p) => p.pattern === 'unclassified');
  const sets = Number(unclassified?.sets) || 0;
  if (sets / total < UNCLASSIFIED_WARNING_SHARE) return null;

  return `${Math.round((sets / total) * 100)}% of this week is unclassified work, `
    + 'so the balance below describes less than half of it.';
}

/** Whether there is anything to balance at all. */
export function hasProgramming(week) {
  return (Number(week?.sets) || 0) > 0;
}

/**
 * The three magnifications of one block.
 *
 * # WHY THERE IS NO MONTH
 *
 * The surface this is modelled on zooms Month → Week → Day, because its blocks
 * are dated: a session sits on the 24th of August. Ours are NOT. A phase is a
 * dateless block of N weeks, and a date only exists once it is assigned to
 * somebody with a start date — see models/schedule.go. A "month" would be a
 * month of nothing.
 *
 * What our block actually has is a third axis they do not: weeks. So the ladder
 * is Block → Week → Day, and each level answers a different question rather than
 * showing the same thing smaller:
 *
 *   Block  every movement across every WEEK      is this getting heavier
 *   Week   every day beside every other DAY      is the week balanced
 *   Day    one session, nothing truncated        is this session right
 */
export const ZOOM = {
  BLOCK: 'block',
  WEEK: 'week',
  DAY: 'day',
};

export const ZOOM_LEVELS = [
  { id: ZOOM.BLOCK, label: 'Block', asks: 'Is this getting heavier' },
  { id: ZOOM.WEEK, label: 'Week', asks: 'Is the week balanced' },
  { id: ZOOM.DAY, label: 'Day', asks: 'Is this session right' },
];

/**
 * Where each rung goes.
 *
 * Day carries WHICH day in the query string rather than the path: it is a lens
 * on the week, not a different resource, and a coach pressing back should return
 * to the week they were looking at rather than to a list.
 */
export function zoomHref({ programId, phaseId, level, dayOfWeek }) {
  const base = `/programs/${programId}/phases/${phaseId}`;
  if (level === ZOOM.BLOCK) return `${base}/grid`;
  // Day zoom without a named day is STILL day zoom. `?day=` is the block view's
  // case — it knows the coach wants one session, not which one — and focusedDay
  // resolves the blank to the first day with work. Dropping the parameter
  // instead would land the Day rung on the unzoomed week: a rung that does not
  // zoom, and one that then draws itself as "Week".
  if (level === ZOOM.DAY) return `${base}/week?day=${dayOfWeek ?? ''}`;
  return `${base}/week`;
}

/**
 * Which day the day-zoom should open on, given the URL and the week.
 *
 * Falls back to the first day that has work rather than to Sunday: a coach
 * zooming in with no day chosen means "the one I am working on", and an empty
 * column is never that.
 */
export function focusedDay(week, requested) {
  const days = orderedDays(week);
  if (days.length === 0) return null;

  // Blank is ABSENT, not zero. Number('') is 0, which is a valid dayOfWeek, so
  // `?day=` would silently resolve to Sunday — usually an empty column, and
  // never the day the coach was working on.
  const given = String(requested ?? '').trim();
  const asNumber = given === '' ? NaN : Number(given);
  if (Number.isInteger(asNumber)) {
    const match = days.find((day) => day.dayOfWeek === asNumber);
    if (match) return match;
  }
  return days.find((day) => (day.sessions ?? []).length > 0) || days[0];
}
